<template>
  <div class="modal-frame modal-medium" :class="{active:isOpen}" data-modal="download-dates">
    <div class="modal-frame-content">
      <button @click="closeModal" class="btn-close" rel="button_close" type="button" data-modal-close>
        <svg class="svg-cross">
          <use href="/img/sprite.svg#svg-cross"></use>
        </svg>
      </button>

      <div class="modal-frame-header">
        <div class="h3">{{ $t('uploads.datesTitle') }}</div>
      </div>

      <div class="modal-frame-body">
        <div class="download-zone" ref="dropzoneElement">
          <div class="progress-group-list" v-if="fileDoc">
            <div class="progress-group">
              <div class="progress-title">
                <p class="font-16">{{ fileDoc.name }} <span class="muted-14">{{ formatFileSize(fileDoc.size) }}</span></p>
                <button class="progress-cansel" @click.stop="removeFile" type="button">
                  <svg class="svg-cross">
                    <use href="/img/sprite.svg#svg-cross"></use>
                  </svg>
                </button>
              </div>
              <div class="progress-line-group">
                <div class="progress-status">
                  <svg v-if="checkCompleted" class="svg-check-fill">
                    <use href="/img/sprite.svg#svg-check-fill"></use>
                  </svg>
                  <div v-else class="font-11">{{ fileDoc.progress }}%</div>
                </div>
                <div class="progress-line">
                  <span :style="{ width: fileDoc.progress + '%' }"></span>
                </div>
              </div>
            </div>
          </div>

          <div class="download-zone-box">
            <div class="download-icon">
              <svg class="svg-load">
                <use href="/img/sprite.svg#svg-load"></use>
              </svg>
            </div>
          </div>

          <div class="download-zone-text attachment-button" v-if="!fileDoc">
            <p>{{ $t('uploads.dragFile') }}
              <a href="#!" class="txt-accent" @click.prevent="openFileDialog">{{ $t('uploads.chooseUpload') }}</a>
            </p>
            <div class="download-zone-conditions">
              <span class="muted-13">{{ $t('uploads.dateFormats') }}</span>
              <span class="muted-13">{{ $t('uploads.maxSize') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="download-zone-note font-14">
        <div class="txt-red">{{ $t('uploads.warningTitle') }}</div>
        <p>{{ $t('uploads.datesWarning1') }}</p>
        <p>{{ $t('uploads.datesWarning2') }}</p>
      </div>

      <div class="modal-frame-body" v-if="serverErrors.length || notFoundRows.length || checkCompleted">
        <div class="modal-wrapper font-15">
          <div class="error-message" v-if="serverErrors.length">
            <div v-for="(error, index) in serverErrors" :key="index">{{ error }}</div>
          </div>

          <template v-if="notFoundRows.length">
            <p class="txt-red txt-center">{{ $t('uploads.rowsNotFound') }}</p>
            <ul class="dates-list font-15">
              <li v-for="row in notFoundRows" :key="row.row">
                {{ $t('uploads.row') }} {{ row.row }}<template v-if="row.article">, {{ $t('uploads.article') }} {{ row.article }}</template><template v-if="row.id">, ID {{ row.id }}</template>
              </li>
            </ul>
          </template>

          <p v-if="checkCompleted && foundCount > 0" class="txt-center">
            {{ $t('uploads.foundRows', { count: foundCount }) }}
          </p>
        </div>
      </div>

      <template v-if="canImport">
        <div class="modal-footer font-18">
          <p class="txt-center">{{ $t('uploads.continue') }}</p>
        </div>
        <div class="flex-center">
          <button class="btn" :class="{loader:isSubmitting}" type="button" @click="confirmImport">{{ $t('modals.yes') }}</button>
          <button class="btn-outline" type="button" @click="removeFile">{{ $t('modals.no') }}</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Dropzone } from 'dropzone';
import 'dropzone/dist/dropzone.css';
import {mapActions} from "vuex";
import { translateApiErrors } from '@/utils/apiErrorUtils';

export default {
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['close', 'imported'],
  data: () => ({
    fileDoc: null,
    dropzoneInstance: null,
    isSubmitting: false,
    checkCompleted: false,
    canImport: false,
    foundCount: 0,
    notFoundRows: [],
    serverErrors: [],
  }),
  mounted() {
    this.initDropzone();
  },
  beforeUnmount() {
    if (this.dropzoneInstance) {
      this.dropzoneInstance.destroy();
    }
  },
  methods: {
    ...mapActions('notify', [
      'addNotify',
    ]),
    initDropzone() {
      this.dropzoneInstance = new Dropzone(this.$refs.dropzoneElement, {
        url: '/',
        method: 'post',
        disablePreviews: true,
        autoProcessQueue: false,
        clickable: false,
        maxFiles: 1,
        maxFilesize: 2,
        acceptedFiles: '.xls,.xlsx',
        addRemoveLinks: false,
        autoQueue: false,
      });

      this.dropzoneInstance.on('addedfile', (file) => {
        if (this.fileDoc) {
          this.dropzoneInstance.removeFile(this.fileDoc.file);
        }

        this.resetCheckState();
        this.fileDoc = {
          name: file.name,
          size: file.size,
          progress: 0,
          file,
        };
        this.checkFile();
      });

      this.dropzoneInstance.on('error', (file, errorMessage) => {
        this.serverErrors = [String(errorMessage)];
        this.dropzoneInstance.removeFile(file);
      });
    },
    openFileDialog() {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = false;
      input.accept = '.xls,.xlsx';

      input.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) {
          this.dropzoneInstance.addFile(file);
        }
      });

      input.click();
    },
    async checkFile() {
      await this.sendFile(false);
    },
    async confirmImport() {
      await this.sendFile(true);
    },
    async sendFile(apply) {
      if (!this.fileDoc) {
        return;
      }

      this.isSubmitting = true;
      this.serverErrors = [];
      if (!apply) {
        this.notFoundRows = [];
        this.foundCount = 0;
        this.canImport = false;
        this.checkCompleted = false;
      }

      const formData = new FormData();
      formData.append('file', this.fileDoc.file);
      formData.append('apply', apply ? '1' : '0');

      try {
        this.fileDoc.progress = apply ? 50 : 30;
        const url = this.id ? `/api/order/import/${this.id}/` : '/api/order/import/';
        const response = await axios.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.status === 'error') {
          this.serverErrors = translateApiErrors(response.data.errors, this.$t);
          return;
        }

        const data = response.data.data || {};
        this.fileDoc.progress = 100;

        if (!apply) {
          this.checkCompleted = true;
          this.canImport = Boolean(data.can_import);
          this.foundCount = Number(data.found || 0);
          this.notFoundRows = Array.isArray(data.not_found) ? data.not_found : [];
          this.serverErrors = Array.isArray(data.errors) ? data.errors.map(error => error.message || String(error)) : [];
          return;
        }

        this.$emit('imported', data);
        this.addNotify({text: this.$t('uploads.importCompleted', { count: this.getImportedRowsCount(data) })});
        this.closeModal();
      } catch (error) {
        this.serverErrors = [error?.message || String(error)];
      } finally {
        this.isSubmitting = false;
      }
    },
    extractErrors(errors) {
      if (!Array.isArray(errors) || !errors.length) {
        return [this.$t('errors.serverError')];
      }
      return translateApiErrors(errors, this.$t);
    },
    getImportedRowsCount(data) {
      return Number(data?.updated || 0) + Number(data?.created || 0);
    },
    removeFile() {
      if (this.fileDoc?.file && this.dropzoneInstance) {
        this.dropzoneInstance.removeFile(this.fileDoc.file);
      }
      this.fileDoc = null;
      this.resetCheckState();
    },
    resetCheckState() {
      this.isSubmitting = false;
      this.checkCompleted = false;
      this.canImport = false;
      this.foundCount = 0;
      this.notFoundRows = [];
      this.serverErrors = [];
    },
    closeModal() {
      this.removeFile();
      this.$emit('close');
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' Kb';
      return Math.round(bytes / (1024 * 1024)) + ' Mb';
    },
  },
}
</script>
